/**
 * Store selector drawer.
 *
 * Open/close mechanics deliberately mirror CartDrawer (cart-drawer.js): the
 * element carries the shared .drawer classes and component-cart-drawer.css,
 * overlay + Escape close it, and the close button uses an inline onclick so
 * closing never depends on listener setup.
 *
 * Selection is per-browser via localStorage. The server always renders the
 * default store; on load the saved choice (if any, and if it still exists in
 * the list) is applied. Selecting a store updates every trigger's label and
 * dispatches "store-selector:change" on document — future per-store features
 * (e.g. product-page inventory) should listen for that event and can also call
 * StoreSelectorDrawer.getSelected().
 */
class StoreSelectorDrawer extends HTMLElement {
  static STORAGE_KEY = 'party-stuff:selected-store';

  // Hours are entered in the store's local time; open/closed is computed
  // against the current time in this zone, so it is correct for every visitor
  // regardless of their own timezone. Displayed times stay Winnipeg-local.
  static TIMEZONE = 'America/Winnipeg';
  static DAY_KEYS = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];

  static getSelected() {
    const drawer = document.querySelector('store-selector-drawer');
    if (!drawer || !drawer.selectedCard) return null;
    return {
      handle: drawer.selectedCard.dataset.storeCard,
      name: drawer.selectedCard.dataset.storeName,
      shopifyLocation: drawer.selectedCard.dataset.shopifyLocation || null,
      locationId: drawer.selectedCard.dataset.locationId || null,
    };
  }

  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#StoreSelector-Overlay').addEventListener('click', this.close.bind(this));

    this.triggers = Array.from(document.querySelectorAll('[data-store-selector-trigger]'));
    this.triggers.forEach((btn) => btn.addEventListener('click', () => this.open(btn)));

    this.querySelectorAll('[data-store-select]').forEach((btn) =>
      btn.addEventListener('click', () => this.select(btn.dataset.storeSelect))
    );

    this.applySelection(this.readStored() || this.dataset.defaultHandle, false);
    setInterval(() => this.updateStatuses(), 60 * 1000);
  }

  open(triggeredBy) {
    if (this.classList.contains('active')) return;
    if (triggeredBy) this.setActiveElement(triggeredBy);
    // Same as CartDrawer: the animation doesn't always trigger without a timeout.
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.querySelector('#StoreSelector');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true },
    );

    document.body.classList.add('overflow-hidden');
    this.triggers.forEach((btn) => btn.setAttribute('aria-expanded', 'true'));
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
    this.triggers.forEach((btn) => btn.setAttribute('aria-expanded', 'false'));
  }

  setActiveElement(element) {
    this.activeElement = element;
  }

  readStored() {
    // localStorage can throw (private browsing, blocked cookies) — treat as unset.
    try {
      return window.localStorage.getItem(StoreSelectorDrawer.STORAGE_KEY);
    } catch {
      return null;
    }
  }

  persist(handle) {
    try {
      window.localStorage.setItem(StoreSelectorDrawer.STORAGE_KEY, handle);
    } catch {
      // Non-fatal: the choice simply won't survive a reload.
    }
  }

  applySelection(handle, notify) {
    let card = handle && this.querySelector(`[data-store-card="${CSS.escape(handle)}"]`);
    // A stale stored handle (store removed/renamed) falls back to the default.
    if (!card) card = this.querySelector(`[data-store-card="${CSS.escape(this.dataset.defaultHandle)}"]`);
    if (!card) card = this.querySelector('[data-store-card]');
    if (!card) return;

    this.selectedCard = card;

    // Keep the selected store at the top of the list.
    if (card.parentElement.firstElementChild !== card) card.parentElement.prepend(card);

    this.querySelectorAll('[data-store-card]').forEach((el) => {
      const isSelected = el === card;
      el.classList.toggle('store-selector__card--selected', isSelected);
      el.querySelector('[data-store-selected-badge]').hidden = !isSelected;
      const btn = el.querySelector('[data-store-select]');
      btn.disabled = isSelected;
      btn.setAttribute('aria-pressed', isSelected ? 'true' : 'false');
    });

    document.querySelectorAll('[data-store-selector-name]').forEach((el) => {
      el.textContent = card.dataset.storeName;
    });

    // Swap any header phone to the selected store's number, falling back to the
    // theme-settings default when that store has no phone.
    const storePhone = (card.dataset.storePhone || '').trim();
    document.querySelectorAll('[data-store-selector-phone]').forEach((el) => {
      const value = storePhone || el.dataset.defaultPhone || '';
      if (!value) return;
      el.textContent = value;
      el.setAttribute('href', 'tel:' + value.replace(/[\s()\-]/g, ''));
    });

    this.updateStatuses();

    if (notify) {
      document.dispatchEvent(
        new CustomEvent('store-selector:change', { detail: StoreSelectorDrawer.getSelected() })
      );
    }
  }

  select(handle) {
    this.persist(handle);
    this.applySelection(handle, true);
    this.close();
  }

  /* --- Open/closed status --- */

  // Current weekday index (into DAY_KEYS) and minutes-since-midnight in the
  // store's timezone. Intl handles the timezone conversion and DST.
  storeNow() {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: StoreSelectorDrawer.TIMEZONE,
      weekday: 'short',
      hour: 'numeric',
      minute: 'numeric',
      hourCycle: 'h23',
    }).formatToParts(new Date());
    const get = (type) => parts.find((p) => p.type === type).value;
    return {
      day: StoreSelectorDrawer.DAY_KEYS.indexOf(get('weekday').slice(0, 3).toLowerCase()),
      minutes: Number(get('hour')) * 60 + Number(get('minute')),
    };
  }

  // "10:00-19:00" → { open, close } in minutes; "closed" → null;
  // blank or malformed → undefined (day is skipped entirely).
  parseHours(value) {
    if (!value) return undefined;
    const v = value.trim().toLowerCase();
    if (v === 'closed') return null;
    const m = v.match(/^(\d{1,2}):(\d{2})\s*-\s*(\d{1,2}):(\d{2})$/);
    if (!m) return undefined;
    const open = Number(m[1]) * 60 + Number(m[2]);
    const close = Number(m[3]) * 60 + Number(m[4]);
    return close > open ? { open, close } : undefined;
  }

  formatTime(minutes) {
    const h = Math.floor(minutes / 60);
    const suffix = h < 12 ? 'a.m.' : 'p.m.';
    return `${h % 12 || 12}:${String(minutes % 60).padStart(2, '0')} ${suffix}`;
  }

  dayName(index) {
    // 2021-08-01 was a Sunday, so day 1 + index lands on DAY_KEYS[index].
    return new Intl.DateTimeFormat(document.documentElement.lang || undefined, {
      weekday: 'short',
      timeZone: 'UTC',
    }).format(new Date(Date.UTC(2021, 7, 1 + index, 12)));
  }

  statusFor(card, now) {
    const sched = StoreSelectorDrawer.DAY_KEYS.map((day) => this.parseHours(card.dataset[`${day}Hours`]));
    if (!sched.some((day) => day !== undefined)) return null;

    const t = this.dataset;
    const today = sched[now.day];
    if (today && now.minutes >= today.open && now.minutes < today.close) {
      const closeTime = this.formatTime(today.close);
      return {
        open: true,
        text: `${t.textOpen} · ${t.textCloses.replace('[time]', closeTime)}`,
        // Shorter phrasing used by the mobile location bar: "Open till 7:00 p.m."
        shortText: t.textOpenTill ? t.textOpenTill.replace('[time]', closeTime) : `${t.textOpen} · ${t.textCloses.replace('[time]', closeTime)}`,
      };
    }
    if (today && now.minutes < today.open) {
      const text = `${t.textClosed} · ${t.textOpens.replace('[time]', this.formatTime(today.open))}`;
      return { open: false, text, shortText: text };
    }
    for (let i = 1; i <= 7; i++) {
      const index = (now.day + i) % 7;
      const next = sched[index];
      if (next) {
        const opensText = t.textOpensDay.replace('[day]', this.dayName(index)).replace('[time]', this.formatTime(next.open));
        const text = `${t.textClosed} · ${opensText}`;
        return { open: false, text, shortText: text };
      }
    }
    return { open: false, text: t.textClosed, shortText: t.textClosed };
  }

  renderStatus(el, status, key) {
    el.textContent = '';
    if (!status) {
      el.hidden = true;
      return;
    }
    const dot = document.createElement('span');
    dot.className = `store-selector__status-dot store-selector__status-dot--${status.open ? 'open' : 'closed'}`;
    el.append(dot, document.createTextNode(status[key] || status.text));
    el.hidden = false;
  }

  updateStatuses() {
    const now = this.storeNow();
    this.querySelectorAll('[data-store-card]').forEach((card) => {
      const el = card.querySelector('[data-store-status]');
      if (el) this.renderStatus(el, this.statusFor(card, now));
    });
    const chipStatus = this.selectedCard ? this.statusFor(this.selectedCard, now) : null;
    // Header chip / drawer use the full wording; the mobile bar uses the short one.
    document.querySelectorAll('[data-store-selector-status]').forEach((el) => this.renderStatus(el, chipStatus, 'text'));
    document.querySelectorAll('[data-store-selector-status-short]').forEach((el) => this.renderStatus(el, chipStatus, 'shortText'));
  }
}

customElements.define('store-selector-drawer', StoreSelectorDrawer);
